import { useMountEffect } from '@timelyapp/react';
import classNames from 'classnames';
import styles from 'components/datepicker/DatePickerCustomPeriod.module.scss';
import {
  handleDisabledDays,
  sanitizeRange,
  setDatesInCorrectOrder,
} from 'components/datepicker/utils';
import IntersectionObserver from 'components/IntersectionObserver';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import type { DayModifiers } from 'react-day-picker';
import type { StartOfWeek } from 'types/account';
import {
  createRangeFromYear,
  getMonthsInYear,
  getNativeDateFromString,
  isBetweenDates,
} from 'utils/date-helper';
import { fromISO, fromJSDate } from 'utils/luxon-helpers';
import type { DatePickerConfig, DatePickerMode, DateRange, MaxRange } from './types';

interface Props {
  mode: Partial<DatePickerMode>;
  baseDate: string;
  returnToDate: string;
  scopeId: string;
  onDayClick: (date: Date, options: Partial<DayModifiers>) => unknown;
  disabledBefore?: string;
  disabledAfter?: string;
  disableNonWorkDays?: boolean;
  customDateRangeEnabled: boolean;
  maxRange?: MaxRange;
  dateRange: DateRange;
  handleCalendarScrollTo: (isIntersecting: boolean, scrollTowards: any) => void;
  dateOffset: number;
  config: DatePickerConfig;
  startOfWeek: StartOfWeek;
  handleMouseEnter: (day: Date) => void;
  enteredTo: string | null;
}
const today = DateTime.now().toISODate();

interface RenderButtonProps extends Pick<Props, 'handleMouseEnter' | 'onDayClick'> {
  date: string;
  mode: DatePickerMode;
  dateClass: string;
  isDisabled: boolean;
  value: string;
  rowHeight: number;
}

const RenderButton: React.FC<RenderButtonProps> = React.memo(
  ({ date, mode, dateClass, rowHeight, isDisabled, handleMouseEnter, onDayClick, value }) => {
    const day = getNativeDateFromString(date);
    return (
      <button
        style={{ height: mode === 'year' ? rowHeight : 'initial' }}
        type="button"
        disabled={isDisabled}
        onClick={() =>
          onDayClick(day, {
            disabled: isDisabled,
          })
        }
        className={dateClass}
        onMouseEnter={() => handleMouseEnter(day)}
      >
        {value}
      </button>
    );
  }
);

const DatePickerCustomPeriod: React.FC<Props> = props => {
  const {
    mode,
    baseDate,
    onDayClick,
    disabledBefore,
    disabledAfter,
    disableNonWorkDays,
    dateOffset,
    handleCalendarScrollTo,
    config,
    maxRange,
    handleMouseEnter,
    enteredTo,
    customDateRangeEnabled,
    returnToDate,
    startOfWeek,
  } = props;

  const [staticReturnToDate, setStaticReturnToDate] = useState<string | null>(null);
  // This is to stop <IntersectionObserver /> from being triggered when we fire the programmatic scrollTo on mount
  const [hasInitialized, setHasInitialized] = useState(false);

  useMountEffect(() => {
    setStaticReturnToDate(returnToDate);
    setHasInitialized(true);
  });

  const { rowHeight, scrollStep } = config;

  const dateRange = useMemo(
    () =>
      setDatesInCorrectOrder({
        from: props.dateRange.from,
        to: enteredTo || props.dateRange.to,
      }),
    [enteredTo, props.dateRange.from, props.dateRange.to]
  );

  const { from, to } = useMemo(
    () =>
      sanitizeRange(
        baseDate,
        mode,
        startOfWeek,
        customDateRangeEnabled ? props.dateRange : undefined
      ),
    [baseDate, customDateRangeEnabled, mode, startOfWeek, props.dateRange]
  );

  const getDisabledCheck = useMemo(
    () =>
      handleDisabledDays({
        disabledBefore,
        disabledAfter,
        disableNonWorkDays,
        dateRange: { from, to },
        maxRange: !props.dateRange.to ? maxRange : undefined,
      }),
    [disableNonWorkDays, disabledAfter, disabledBefore, from, maxRange, props.dateRange.to, to]
  );

  const isMakingSelection = enteredTo != null;
  const startFrom = useMemo(
    () => fromISO(returnToDate).plus({ years: dateOffset }),
    [dateOffset, returnToDate]
  );
  const monthIndexes = useMemo(() => Array.from(Array(scrollStep * 2).keys()), [scrollStep]);

  const renderButtons = (date: string) => {
    const dtDate = fromISO(date);
    const value = mode === 'year' ? date?.substring?.(0, 4) : dtDate.toFormat('MMM');
    const isActive =
      dateRange.to && customDateRangeEnabled
        ? isBetweenDates(date, dateRange.from.substring(0, 10), dateRange.to.substring(0, 10))
        : baseDate === date;
    const isDisabled = getDisabledCheck(dtDate.toJSDate());

    const dateClass = classNames(styles.date, {
      [styles.isActive]: isActive,
      [styles.isDisabled]: isDisabled,
      [styles.isCurrent]: dtDate.hasSame(fromISO(today), mode),
      [styles.isYearMode]: mode === 'year',
      [styles.firstInSelection]: dtDate.hasSame(fromISO(dateRange.from), mode),
      [styles.lastInSelection]:
        dateRange.to && customDateRangeEnabled
          ? dtDate.hasSame(fromISO(dateRange.to), mode)
          : baseDate === date,
      [styles.unfinishedSelection]: isActive && isMakingSelection,
    });

    return (
      <RenderButton
        key={date}
        date={date}
        mode={mode}
        dateClass={dateClass}
        rowHeight={rowHeight}
        isDisabled={isDisabled}
        handleMouseEnter={handleMouseEnter}
        onDayClick={onDayClick}
        value={value}
      />
    );
  };

  if (mode === 'year') {
    if (!staticReturnToDate) {
      return null;
    }
    const startFrom = fromISO(staticReturnToDate).startOf('year').minus({ years: 15 }).toISODate();
    const years = createRangeFromYear(30, startFrom);

    return (
      <div className={styles.container}>
        <div className={classNames(styles.dateGrid, { [styles.years]: mode === 'year' })}>
          {years.map(renderButtons)}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {hasInitialized && (
        <IntersectionObserver onIntersectingChange={handleCalendarScrollTo} context="past">
          <div style={{ height: 10 }} />
        </IntersectionObserver>
      )}
      {monthIndexes.map(idx => {
        const referenceDate = new Date(startFrom.plus({ years: idx }).toISO());
        const dates = getMonthsInYear(referenceDate);
        const id = fromJSDate(referenceDate).startOf('month').toISODate();
        return (
          <div
            style={{ display: 'flex', height: rowHeight, marginBottom: 20, position: 'relative' }}
            id={id}
            key={idx}
          >
            <div className={styles.header}>{referenceDate.getFullYear()}</div>
            <div className={classNames(styles.dateGrid, styles.months)}>
              {dates.map(renderButtons)}
            </div>
          </div>
        );
      })}
      {hasInitialized && (
        <IntersectionObserver onIntersectingChange={handleCalendarScrollTo} context="future">
          <div style={{ height: 10 }} />
        </IntersectionObserver>
      )}
    </div>
  );
};

export default DatePickerCustomPeriod;
